import { useId, useState, type ReactNode } from 'react';
import {
  Badge,
  Icon,
  IconButton,
  IconRail,
  Modal,
  RailPanel,
  ResizablePane,
  Tabs,
  Tooltip,
  icons,
  theme,
} from '@agentisai/design-system';
import { useSearchParams } from 'react-router';
import { usePersistedWidth } from '../hooks/usePersistedWidth';
import { PANEL_PARAM } from './WorkstreamsRailBody';
import { useViewportTier } from '../hooks/useViewportTier';
import {
  hamburgerBar as hamburgerBarCls,
  main as mainCls,
  railSlot as railSlotCls,
  shell as shellCls,
} from './WorkspaceShell.css';

/** The five canonical rail panels. Only `workstreams` is openable in Drop 5. */
type PanelId = 'workstreams' | 'deliverables' | 'files' | 'agents' | 'memory';

interface RailEntry {
  readonly id: PanelId;
  readonly label: string;
  readonly icon: ReactNode;
  /** "Coming soon" placeholder buttons — focusable but `aria-disabled`. */
  readonly placeholder: boolean;
}

/**
 * Overlay a count badge on a rail glyph. A muted zero rather than a hidden
 * badge: "does this workstream have files?" is the question the rail is being
 * glanced at to answer, and a suppressed badge answers it only by absence —
 * which reads the same as "not loaded yet". `aria-hidden` because the count is
 * already in the button's accessible name.
 */
function withCountBadge(icon: ReactNode, count: number): ReactNode {
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      {icon}
      <Badge
        tone={count > 0 ? 'info' : 'neutral'}
        size="sm"
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: `calc(-1 * ${theme.space['1_5']})`,
          insetInlineEnd: `calc(-1 * ${theme.space['2_5']})`,
          pointerEvents: 'none',
        }}
      >
        {count}
      </Badge>
    </span>
  );
}

/**
 * Static rail entries (icons + tooltip labels) per the DS `IconRail` story and
 * the agentis2 prototype 1:1. Only Workstreams is wired in Drop 5; the other
 * four are placeholders.
 */
const RAIL_ITEMS: readonly RailEntry[] = [
  {
    id: 'workstreams',
    label: 'Workstreams',
    icon: <Icon as={icons.LayoutGrid} size={20} />,
    placeholder: false,
  },
  {
    id: 'deliverables',
    label: 'Deliverables',
    icon: <Icon as={icons.FileText} size={20} />,
    placeholder: true,
  },
  {
    // Files is live from Drop 6 (S08) — but only when a workstream is selected;
    // the id-based branch in the rail map handles its gated-vs-live states, so
    // it is not a "coming soon" placeholder.
    id: 'files',
    label: 'Files',
    icon: <Icon as={icons.Folder} size={20} />,
    placeholder: false,
  },
  {
    // Live from AB-2-2 (ADR-0080): the caller's drafts + New agent. Like Memory
    // it opens with nothing selected — drafts aren't scoped to a workstream.
    id: 'agents',
    label: 'Agents',
    icon: <Icon as={icons.Users} size={20} />,
    placeholder: false,
  },
  {
    // Memory is live from ADR-0056. UNLIKE Files it is reachable with nothing
    // selected — the panel is always mounted and its body explains the surface
    // rather than showing an empty list (see the panel comment below). Not a
    // "coming soon" placeholder.
    id: 'memory',
    label: 'Workstream Memory',
    icon: <Icon as={icons.Lightbulb} size={20} />,
    placeholder: false,
  },
] as const;

/**
 * The rail glyph for a panel id. One lookup so a `RailPanel`'s icon and the
 * `IconRail.Button` that opens it are physically the same value — the whole
 * point is that they match, and two hand-written copies would eventually not.
 */
function railIcon(id: PanelId): ReactNode {
  return RAIL_ITEMS.find((item) => item.id === id)?.icon ?? null;
}

/** Rail-panel width bounds (px). The default is the DS `RailPanel` width; widen-only. */
const RAIL_MIN = theme.size.railPanelWidth;
const RAIL_MAX = 480;

export interface WorkspaceShellProps {
  /**
   * Main pane content — sits to the right of the rail + active panel.
   * The shell itself is layout chrome; the surface it frames (empty state,
   * artifact workspace, etc.) is the consumer's concern.
   */
  readonly children: ReactNode;
  /**
   * Rail-panel body — typically the workstream list/bubbles (C3). When the
   * Workstreams panel is collapsed this is rendered hidden (the panel mounts
   * once and toggles via `hidden`, so React Query state survives a collapse).
   */
  readonly workstreamsPanel?: ReactNode;
  /** Right-aligned action in the Workstreams subheader (e.g. `+ New`, S-C3). */
  readonly workstreamsAction?: ReactNode;
  /**
   * Files rail-panel body (Drop 6 / S09). Rendered inside the Files `RailPanel`
   * when the Files icon is opened — which is only reachable when a workstream is
   * selected. Mounted only while a workstream is selected so its per-workstream
   * query (S09) keys correctly and tears down on deselect.
   */
  readonly filesPanel?: ReactNode;
  /**
   * Number of files in the selected workstream, for the rail's count badge.
   * `null` while no workstream is selected or the count has not loaded — the
   * badge is suppressed rather than guessing zero, since "no workstream" and
   * "an empty workstream" are different states and only one of them is a fact.
   */
  readonly filesCount?: number | null;
  /** Right-aligned action in the Files subheader (the inert "+ New" pill, S10). */
  readonly filesAction?: ReactNode;
  /**
   * Memory rail-panel body (ADR-0056). Rendered inside the Memory `RailPanel`,
   * which — unlike Files — is deliberately NOT gated on a selected workstream:
   * the icon always opens, and the body explains the surface when nothing is
   * selected rather than presenting an inert icon the user cannot interrogate.
   * Consequently it is ALWAYS MOUNTED (hidden on collapse, so the query cache
   * survives), and the body owns the `workstreamId === null` case itself.
   */
  readonly memoryPanel?: ReactNode;
  /**
   * Agents rail-panel body (ADR-0080, AB-2-2) — the caller's drafts + New agent.
   * Always mounted (hidden on collapse) like Memory: it needs no workstream.
   */
  readonly agentsPanel?: ReactNode;
  /**
   * The currently-active workstream id (the `?ws=` selection, threaded from the
   * page). Gates the Files rail icon: absent → the icon is disabled with a
   * "select a workstream" tooltip; present → the icon opens the Files panel.
   */
  readonly selectedWorkstreamId?: string | null;
  /**
   * Rail entries below a divider, after the workstream panels — page-level
   * destinations rather than panel disclosures (e.g. the staff-only Evals
   * link, ADR-0072). The consumer decides visibility; absent → no divider.
   * At the compact tier (no rail) they render beside the hamburger instead.
   */
  readonly railFooter?: ReactNode;
}

/**
 * WorkspaceShell — composes the DS `IconRail` + `RailPanel` into the real
 * authenticated workspace (S-C2).
 *
 * A11y model is the DS disclosure pattern (M11): the active rail button
 * carries `aria-expanded` + `aria-controls` pointing at the panel's id. The
 * placeholder button (Deliverables) and the
 * gated Files button are NOT native-disabled — that would suppress pointer +
 * focus and hide their tooltip from keyboard + screen-reader users — they carry
 * `aria-disabled="true"`, stay focusable, and click is a no-op.
 *
 * Panel state is a single mutually-exclusive machine keyed by `PanelId`:
 * `Workstreams` is always live; `Files` becomes live once a workstream is
 * selected (Drop 6 / S08). Deselecting a workstream while Files is open collapses
 * it by derivation (no effect), never leaving an open-but-inert panel.
 */
export function WorkspaceShell({
  children,
  workstreamsPanel,
  workstreamsAction,
  filesPanel,
  filesCount = null,
  filesAction,
  memoryPanel,
  agentsPanel,
  selectedWorkstreamId,
  railFooter,
}: WorkspaceShellProps) {
  // Mutually-exclusive panel state, in the URL (`?panel=`, R3) so it survives the
  // page remounting this shell on a workstream switch — opening a draft from the
  // Agents panel keeps the Agents panel open. Absent = Workstreams (the primary
  // surface; landing collapsed would hide the create flow C3 wires into it),
  // `none` = all collapsed; an unknown value reads as absent.
  const [params, setParams] = useSearchParams();
  const rawPanel = params.get(PANEL_PARAM);
  const openPanel: PanelId | null =
    rawPanel === 'none'
      ? null
      : (RAIL_ITEMS.find((item) => item.id === rawPanel)?.id ?? 'workstreams');

  // Distinct stable ids per panel for `aria-controls` ↔ panel id wiring.
  const workstreamsPanelId = useId();
  const filesPanelId = useId();
  const memoryPanelId = useId();
  const agentsPanelId = useId();

  // Files is gated on a selected workstream: with none, there is no ledger to
  // list and nothing to say about it. Memory is NOT gated — its panel explains
  // itself when nothing is selected, so a user can always open the rail and see
  // what the surface is rather than meeting an inert icon.
  const filesEnabled = Boolean(selectedWorkstreamId);

  // Derived open-state (frontend-architecture R2 — computed in render, no
  // effect): Files can only be *shown* open when it is enabled, so deselecting
  // the workstream collapses an open Files panel by computation.
  function isPanelOpen(id: PanelId): boolean {
    if (id === 'files') return openPanel === 'files' && filesEnabled;
    return openPanel === id;
  }

  function toggle(id: PanelId) {
    const value = isPanelOpen(id) ? 'none' : id;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === 'workstreams') next.delete(PANEL_PARAM);
        else next.set(PANEL_PARAM, value);
        return next;
      },
      { replace: true },
    );
  }
  // Deselecting a workstream drops `panel=files` in the same URL write
  // (`selectWorkstreamParams`), so a later re-select doesn't spring Files back open.

  const isOpen = isPanelOpen('workstreams');

  // The panels are mutually exclusive and share one slot, so they share one
  // width (R1.0 — local to the shell), remembered per device across reloads.
  const [railWidth, setRailWidth, persistRailWidth] = usePersistedWidth('agentis.railPanelWidth');
  const railPane = {
    edge: 'end',
    handleLabel: 'Resize side panel',
    min: RAIL_MIN,
    max: RAIL_MAX,
    width: railWidth,
    onWidthChange: setRailWidth,
    onResizeEnd: persistRailWidth,
  } as const;

  // S-C4 — compact-tier drawer state. The hamburger button (rendered only at
  // compact via CSS) opens the same workstreamsPanel content inside a Modal
  // using `RailPanel fluid` per the canonical recipe. The Modal's `useDialog`
  // handles focus trap + Escape + body scroll lock (DS contract).
  const tier = useViewportTier();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Derive (don't sync via useEffect → setState — react-hooks lint blocks
  // cascading renders): if the viewport flips to regular the desktop rail
  // owns the panel, so the centred drawer collapses by computation, not by
  // an effect that races the next render.
  const drawerVisible = drawerOpen && tier === 'compact';
  // The drawer carries the panels a phone can use, not just Workstreams — it has no rail, so
  // this is its only way to Agents (Forge) and Memory. Same `?panel=` state as the rail;
  // anything else shows Workstreams. ponytail: no Files here — opening a file needs the
  // centre-pane viewer, which is regular-tier only; add Files with a compact viewer.
  const drawerPanel = openPanel === 'agents' || openPanel === 'memory' ? openPanel : 'workstreams';
  const drawerPanels = {
    workstreams: {
      title: 'Workstreams',
      subheaderLabel: 'MY WORKSTREAMS',
      action: workstreamsAction,
      body: workstreamsPanel,
    },
    agents: { title: 'Agents', subheaderLabel: 'MY DRAFTS', action: undefined, body: agentsPanel },
    memory: {
      title: 'Workstream Memory',
      subheaderLabel: "WHAT WE'VE LEARNED",
      action: undefined,
      body: memoryPanel,
    },
  } satisfies Record<
    'workstreams' | 'agents' | 'memory',
    { title: string; subheaderLabel: string; action: ReactNode; body: ReactNode }
  >;
  const drawer = drawerPanels[drawerPanel];
  function showInDrawer(id: string) {
    // Read back through RAIL_ITEMS (openPanel), so only a known panel ever shows.
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id === 'workstreams') next.delete(PANEL_PARAM);
        else next.set(PANEL_PARAM, id);
        return next;
      },
      { replace: true },
    );
  }

  return (
    <div className={shellCls}>
      {/* Rail + panel hidden under `lg` (1024px) — compact-tier nav is C4 (PLAN).
          The slot stays in the DOM at all viewports so React Query / shell
          state doesn't tear when the viewport crosses the breakpoint mid-session.
          Without this, the 48px rail + 280px panel force horizontal overflow at
          375px and the S-F2 e2e fails. */}
      <div className={railSlotCls}>
        <IconRail>
          {RAIL_ITEMS.map((item) => {
            // Files (Drop 6 / S08) is gated: live only when a workstream is
            // selected. With none selected it reads like a placeholder — focusable
            // + aria-disabled + a "select a workstream" tooltip, click inert. Uses
            // the same DS `Tooltip` as the live items (not the native `title`)
            // so a disabled rail icon doesn't look like a different, unstyled
            // control next to its live siblings.
            if (item.id === 'files' && !filesEnabled) {
              return (
                <Tooltip key={item.id} content="Select a workstream to view its files">
                  <IconRail.Button
                    aria-label={`${item.label} (select a workstream first)`}
                    aria-disabled="true"
                    icon={item.icon}
                    onClick={(e) => {
                      e.preventDefault();
                    }}
                  />
                </Tooltip>
              );
            }
            if (item.placeholder) {
              // aria-disabled (not native `disabled`) so the button stays in the
              // tab order + receives focus + dispatches synthetic clicks; the
              // onClick is a no-op so the placeholder doesn't act. Wrapped in the
              // same DS `Tooltip` as the live items — a focusable, aria-disabled
              // button is exactly what `Tooltip` needs for keyboard/AT access,
              // and it keeps every rail icon's tooltip visually consistent.
              return (
                <Tooltip key={item.id} content={`${item.label} — coming soon`}>
                  <IconRail.Button
                    aria-label={`${item.label} (coming soon)`}
                    aria-disabled="true"
                    icon={item.icon}
                    onClick={(e) => {
                      // Mirrors the spec's "remains focusable, click is inert"
                      // behaviour — never advance state from a placeholder.
                      e.preventDefault();
                    }}
                  />
                </Tooltip>
              );
            }
            // Live disclosures — Workstreams (always) + Files (with a workstream).
            const controls =
              item.id === 'files'
                ? filesPanelId
                : item.id === 'memory'
                  ? memoryPanelId
                  : item.id === 'agents'
                    ? agentsPanelId
                    : workstreamsPanelId;
            const showCount = item.id === 'files' && filesCount !== null;
            return (
              <Tooltip key={item.id} content={item.label}>
                <IconRail.Button
                  // The count rides in the accessible name, not the badge, so a
                  // screen reader hears "Files, 3 files" once instead of the
                  // glyph label and a loose number.
                  aria-label={
                    showCount
                      ? `${item.label}, ${filesCount} ${filesCount === 1 ? 'file' : 'files'}`
                      : item.label
                  }
                  active={isPanelOpen(item.id)}
                  aria-controls={controls}
                  icon={showCount ? withCountBadge(item.icon, filesCount) : item.icon}
                  onClick={() => toggle(item.id)}
                />
              </Tooltip>
            );
          })}
          {railFooter && (
            <>
              <IconRail.Divider />
              {railFooter}
            </>
          )}
        </IconRail>
        {/* Panel stays mounted so its useQuery (C1) keeps its cache across a
          collapse; `hidden` hides it from layout + the a11y tree at once. */}
        <ResizablePane {...railPane} hidden={!isOpen}>
          <RailPanel
            id={workstreamsPanelId}
            title="Workstreams"
            // Same glyph as the rail button that opens it — read from
            // RAIL_ITEMS, so the pair cannot drift.
            icon={railIcon('workstreams')}
            subheaderLabel="MY WORKSTREAMS"
            subheaderAction={workstreamsAction}
          >
            {workstreamsPanel}
          </RailPanel>
        </ResizablePane>
        {/* Files panel (Drop 6). Mounted only while a workstream is selected so
          its per-workstream query (S09) keys correctly and tears down on
          deselect; `hidden` toggles visibility on collapse without unmounting. */}
        {filesEnabled && (
          <ResizablePane {...railPane} hidden={!isPanelOpen('files')}>
            <RailPanel
              id={filesPanelId}
              title="Files"
              icon={railIcon('files')}
              subheaderLabel="Files"
              subheaderAction={filesAction}
            >
              {filesPanel}
            </RailPanel>
          </ResizablePane>
        )}
        {/* Memory panel (ADR-0056). Always mounted — unlike Files it is
          reachable with nothing selected, and its body explains the surface
          instead of showing an empty list. `hidden` on collapse so the cache
          survives. */}
        <ResizablePane {...railPane} hidden={!isPanelOpen('memory')}>
          <RailPanel
            id={memoryPanelId}
            title="Workstream Memory"
            icon={railIcon('memory')}
            subheaderLabel="WHAT WE'VE LEARNED"
          >
            {memoryPanel}
          </RailPanel>
        </ResizablePane>
        <ResizablePane {...railPane} hidden={!isPanelOpen('agents')}>
          <RailPanel
            id={agentsPanelId}
            title="Agents"
            icon={railIcon('agents')}
            subheaderLabel="MY DRAFTS"
          >
            {agentsPanel}
          </RailPanel>
        </ResizablePane>
      </div>
      <main className={mainCls}>
        {/* Compact-only hamburger — CSS hides it ≥md so it never appears
            alongside the desktop rail. Opens the fluid RailPanel drawer. */}
        <div className={hamburgerBarCls}>
          <IconButton
            icon={<Icon as={icons.Menu} />}
            aria-label="Open navigation"
            aria-expanded={drawerVisible}
            size="sm"
            variant="ghost"
            onClick={() => setDrawerOpen(true)}
          />
          {/* The rail is hidden at compact, so its page links (Agent Monitor,
              Evals) ride beside the hamburger instead — otherwise a phone has no
              way in. Gated on the tier in JS, not CSS, so the desktop tree never
              holds a second copy of each link. */}
          {tier === 'compact' && railFooter}
        </div>
        {children}
      </main>
      {/* The compact drawer. Modal owns focus trap / Escape / body scroll
          lock (useDialog), and `flushBody` lets RailPanel reach the dialog
          edges so its own header is the visible chrome (no duplicated
          "Workstreams" title from Modal). aria-label provides the dialog
          name since we suppress Modal's title. */}
      <Modal
        open={drawerVisible}
        onClose={() => setDrawerOpen(false)}
        aria-label="Navigation"
        flushBody
      >
        <Tabs
          aria-label="Panels"
          variant="lozenge"
          items={[
            { value: 'workstreams', label: 'Workstreams' },
            { value: 'agents', label: 'Agents' },
            { value: 'memory', label: 'Memory' },
          ]}
          value={drawerPanel}
          onChange={showInDrawer}
          style={{ paddingInline: theme.space[4] }}
        />
        <RailPanel
          title={drawer.title}
          subheaderLabel={drawer.subheaderLabel}
          subheaderAction={drawer.action}
          icon={railIcon(drawerPanel)}
          fluid
        >
          {drawer.body}
        </RailPanel>
      </Modal>
    </div>
  );
}
WorkspaceShell.displayName = 'WorkspaceShell';
